import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ApiService } from "../services";
import { setRedeemablePoints } from "../store";
import { fireGtagEvent } from "../utils";
import { useApplyDiscountsMutation } from "./checkout-mutation.hook";

const calculateRedeemPoints = (pointsRes, userDetails, applyDiscountsApi) => {
  if (!pointsRes?.points || !userDetails?.pointBalance) return null;

  let redeemPoints = Math.min(userDetails.pointBalance, pointsRes.points);

  if (applyDiscountsApi?.discountedCart?.totalLoyaltyDiscountPossible) {
    redeemPoints =
      applyDiscountsApi.discountedCart.totalLoyaltyDiscountPossible;
  }

  return redeemPoints;
};

export const useGetRedeemablePoints = (detail, dispatch) => {
  const { user, order } = useSelector((state) => state);

  return useQuery({
    queryKey: ["get-redeemable-points", detail?.checkoutId],
    queryFn: async () => {
      const response = await ApiService.get(
        `${order.shopDomain}/order/${detail?.checkoutId}/redeemable-points`,
        {
          authorization: `Bearer ${user.authToken}`
        }
      );

      dispatch(setRedeemablePoints(response));
      return response.data;
    },
    enabled: !!user.authToken
  });
};

export const useLoyaltyPoints = () => {
  const dispatch = useDispatch();
  const { authToken, details: userDetails } = useSelector(
    (state) => state.user
  );
  const { checkoutDetails, applyDiscountsApi } = useSelector(
    (state) => state.cart
  );
  const { data: pointsRes } = useGetRedeemablePoints(checkoutDetails, dispatch);
  const { mutate: applyDiscountCodeMutation, status: couponStatus } =
    useApplyDiscountsMutation();

  const redeemPoints = useMemo(
    () => calculateRedeemPoints(pointsRes, userDetails, applyDiscountsApi),
    [pointsRes, userDetails, applyDiscountsApi]
  );

  const applyPoints = useCallback(({ loyaltyPoints, discountCode }) => {
    applyDiscountCodeMutation({ loyaltyPoints, discountCode });
    fireGtagEvent("bb_points_applied", { loyaltyPoints });
  }, []);

  const removePoints = useCallback(({ discountCode, loyaltyPoints }) => {
    applyDiscountCodeMutation({ loyaltyPoints, discountCode });
  }, []);

  return {
    couponStatus,
    authToken,
    userDetails,
    redeemPoints,
    pointsRes,
    applyDiscountsApi,
    applyPoints,
    removePoints
  };
};
